import { useTranslation } from "react-i18next";
import type { SdpStatusEntry } from "../../api/contractStatus";
import { statusChipStyles } from "./ContractStatusChip";

// FT-1189 ⑤ — contract history for one SDP. Backend delivers contracts[] newest
// first (frozen §3); we render them oldest → latest (FT-4685/FT-4991). No
// pagination v1; per-SDP contract counts are small.

interface ContractHistoryProps {
  entry: SdpStatusEntry | undefined;
  loading?: boolean;
}

const fmt = (iso: string | null) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export const ContractHistory = ({ entry, loading }: ContractHistoryProps) => {
  const { t } = useTranslation("ActiveServices");

  if (loading) return null;
  if (!entry || entry.contracts.length === 0) return null;

  const contracts = [...entry.contracts].reverse();

  return (
    <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-primary-700 dark:text-white">{t("contractHistory.title")}</h2>
        {entry.data_flags.length > 0 && (
          <span
            className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1 cursor-help"
            title={t("contractStatus.dataFlags", { flags: entry.data_flags.join(", ") })}
          >
            {t("contractHistory.dataNotice")}
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-primary-400 dark:text-primary-300">
              <th className="py-2 pr-6 font-medium">{t("contractHistory.contractId")}</th>
              <th className="py-2 pr-6 font-medium">{t("contractHistory.startDate")}</th>
              <th className="py-2 pr-6 font-medium">{t("contractHistory.endDate")}</th>
              <th className="py-2 font-medium">{t("contractHistory.status")}</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((contract) => (
              <tr key={contract.contract_id} className="border-t border-gray-200 dark:border-primary-800 text-primary-600 dark:text-primary-100">
                <td className="py-2.5 pr-6">{contract.contract_id}</td>
                <td className="py-2.5 pr-6">{fmt(contract.start_date)}</td>
                <td className="py-2.5 pr-6">{fmt(contract.end_date)}</td>
                <td className="py-2.5">
                  <span className={`px-2 py-1 rounded-md inline-block ${statusChipStyles(contract.status)}`}>
                    {t(`contractStatus.${contract.status.toLowerCase()}`, {
                      defaultValue: contract.status,
                    })}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
